import React, { useState } from 'react';
import { RulesetForm } from './RulesetForm';
import { Box } from '@mui/material';
import { ToastType, useToast } from 'services/toast';
import { Loading } from 'common/ui/Loading';
import {
  useGetAnalysisRequirementsByCandidateBatchGroupQuery,
  useGetAutomationTasksByCandidateBatchGroupQuery,
  useUpdateCandidateBatchGroupComponentsMutation,
  useUpdateCandidateBatchGroupMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { AnalysisAutomationItem } from 'modules/analyse/types/AutomationTasks';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { AnalysisRequirementItem } from 'modules/analyse/types/AnaylsisRequirementItem';
import { useDrawer } from 'services/drawer/hooks';
import { useConfirmationModal } from 'services/modals/hooks';
import { SubmittedRulesetFormValues } from 'modules/analyse/types/RulesetFormValues';
import { validateAnalysisAutomationTasks } from 'modules/analyse/utils/validateAutomationTasks';
import { formatAutomationTasksForSubmission } from 'modules/analyse/utils/formatAutomationTasksForSubmission';
import { normalizeBetweenScoreConditions } from 'modules/analyse/utils/normalizeBetweenScoreConditions';
import { formatAnalysisRequirementsForSubmission } from 'modules/analyse/utils/formatAnalysisRequirementsForSubmission';
import { splitAutomationItem } from 'core/utils/splitAutomationItem';
import { formatAnalysisRequirementItemToInputWithKey } from '../utils/formatAnalysisRequirementItemToInputWithKey';

type Props = {
  candidateBatchGroup: {
    id: string;
    name: string;
  };
};

export const CandidateRankingGroupEditingDrawer: React.FC<Props> = ({
  candidateBatchGroup
}) => {
  const { name, id } = candidateBatchGroup;
  const { showToast } = useToast();
  const { closeDrawer } = useDrawer();
  const { showModal } = useConfirmationModal();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [initialAnalysisRequirements, setInitialAnalysisRequirements] =
    useState<AnalysisRequirementItem[]>([]);
  const [automationRulesTasks, setAutomationRulesTasks] = useState<
    AnalysisAutomationItem[]
  >([]);
  const [automationActionTasks, setAutomationActionTasks] = useState<
    AnalysisAutomationItem[]
  >([]);

  const [updateCandidateBatchGroupComponentsMutation] =
    useUpdateCandidateBatchGroupComponentsMutation();
  const [updateCandidateBatchGroupMutation] =
    useUpdateCandidateBatchGroupMutation();

  const { loading: loadingAutomationsTasks } =
    useGetAutomationTasksByCandidateBatchGroupQuery({
      variables: {
        candidateBatchGroupId: id
      },
      onCompleted: (data) => {
        const result = extractQueryListItems(data);

        const { rulesTasks, actionTasks } = splitAutomationItem(result);

        setAutomationActionTasks(actionTasks);
        setAutomationRulesTasks(rulesTasks);
      }
    });

  const { loading: loadingRequirements } =
    useGetAnalysisRequirementsByCandidateBatchGroupQuery({
      variables: {
        candidateBatchGroupId: id
      },
      onCompleted: (data) => {
        const result = extractQueryListItems(data);

        setInitialAnalysisRequirements(result ?? []);
      }
    });

  const handleFormSubmit = async (
    values: SubmittedRulesetFormValues,
    { skipPastCandidatesUpdate }: { skipPastCandidatesUpdate: boolean }
  ) => {
    try {
      const automationTasks = normalizeBetweenScoreConditions([
        ...values.ruleAutomationTasks,
        ...values.actionAutomationTasks
      ]);

      const initialAutomationTasks = [
        ...automationRulesTasks,
        ...automationActionTasks
      ];

      const { isAllTasksValid, isDuplicates } = validateAnalysisAutomationTasks(
        automationTasks || []
      );

      if (!isAllTasksValid) {
        showToast({
          message: 'You have some invalid automation tasks',
          type: ToastType.Error
        });
        return;
      }

      if (isDuplicates) {
        showToast({
          message:
            'You have conflicting automations. Please remove them before saving.',
          type: ToastType.Error
        });
        return;
      }

      setIsSubmitting(true);

      const {
        automationTasksToCreate,
        automationTasksToDelete,
        automationTasksToUpdate
      } = formatAutomationTasksForSubmission(
        automationTasks || [],
        initialAutomationTasks || []
      );

      const {
        requirementsToCreate,
        requirementsToDelete,
        requirementsToUpdate
      } = formatAnalysisRequirementsForSubmission(
        values.analysisRequirements || [],
        initialAnalysisRequirements || []
      );

      await updateCandidateBatchGroupComponentsMutation({
        variables: {
          input: {
            candidateBatchGroupId: id,
            parentAnalysisRequirementsToAdd: requirementsToCreate,
            parentAnalysisRequirementsToDelete: requirementsToDelete,
            parentAnalysisRequirementsToUpdate: requirementsToUpdate,

            parentAnalysisAutomationTasksToAdd: automationTasksToCreate,
            parentAnalysisAutomationTasksToDelete: automationTasksToDelete,
            parentAnalysisAutomationTasksToUpdate: automationTasksToUpdate,

            skipPastCandidatesUpdate
          }
        }
      });

      if (values.groupName !== name) {
        await updateCandidateBatchGroupMutation({
          variables: {
            input: {
              id,
              name: values.groupName
            }
          }
        });
      }

      showToast({
        message: `${values.groupName || 'Your Ruleset'} has been updated`,
        type: ToastType.Success
      });

      closeDrawer();
    } catch (e) {
      showToast({ message: 'Something went wrong', type: ToastType.Error });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingRequirements || loadingAutomationsTasks) {
    return <Loading />;
  }

  return (
    <Box p={2} width="750px">
      <RulesetForm
        isVerticalForm
        hideTemplateCreateButton={true}
        onFormSubmitted={async (values) => {
          showModal({
            title: 'Save and apply ruleset?',
            message:
              'Apply changes only to future applicants, or to all past and future applicants?',
            confirmationText: 'Future',
            cancelText: 'Cancel',
            showSecondaryButton: true,
            secondaryText: 'All',
            onSecondaryClick: async () => {
              await handleFormSubmit(values, {
                skipPastCandidatesUpdate: false
              });
            },
            onConfirm: async () => {
              await handleFormSubmit(values, {
                skipPastCandidatesUpdate: true
              });
            }
          });
        }}
        initialValues={{
          groupName: name,
          analysisRequirements: formatAnalysisRequirementItemToInputWithKey(
            initialAnalysisRequirements || []
          ),
          actionAutomationTasks: automationActionTasks,
          ruleAutomationTasks: automationRulesTasks
        }}
        isSubmitting={isSubmitting}
        onCanceled={closeDrawer}
      />
    </Box>
  );
};
